interface XRange {
  readonly x0: number;
  readonly x1: number;
}

interface NodeWithDepth {
  readonly __depth?: number;
  /**
   * The visual column index from the align function (e.g. sankeyJustify). Used
   * as the map key when present so sink/source nodes repositioned by justify
   * don't corrupt the x-range of their assigned depth column.
   */
  readonly __layer?: number;
  readonly __x0?: number;
  readonly __x1?: number;
}

/**
 * Builds a map from visual column index to the x-range covering all nodes in
 * that column. Uses `__layer` as the key when available (the actual column
 * assigned by the align function), falling back to `__depth`. When multiple
 * nodes share the same column, the map stores the union of their x-extents.
 */
export function buildDepthToXRangeMap(
  nodes: ReadonlyArray<NodeWithDepth>,
): Map<number, XRange> {
  const map = new Map<number, XRange>();

  for (const node of nodes) {
    const key = node.__layer ?? node.__depth;
    if (
      key === undefined ||
      node.__x0 === undefined ||
      node.__x1 === undefined
    ) {
      continue;
    }

    const existing = map.get(key);
    if (existing === undefined) {
      map.set(key, { x0: node.__x0, x1: node.__x1 });
    } else {
      map.set(key, {
        x0: Math.min(existing.x0, node.__x0),
        x1: Math.max(existing.x1, node.__x1),
      });
    }
  }

  return map;
}

/**
 * Returns the x-range for the given column depth index.
 *
 * - If the depth has layout nodes, returns their x-range directly.
 * - If the depth is within the range of known depths but has no nodes,
 *   linearly interpolates between the nearest known neighbors.
 * - If the depth falls outside [minDepth, maxDepth], returns `undefined`
 *   so the caller can silently skip it
 */
export function getXRangeForDepth(
  depth: number,
  depthToXRange: Map<number, XRange>,
  sortedDepths: ReadonlyArray<number> = [...depthToXRange.keys()].sort(
    (a, b) => a - b,
  ),
): XRange | undefined {
  const direct = depthToXRange.get(depth);
  if (direct !== undefined) {
    return direct;
  }

  if (depthToXRange.size === 0) {
    return undefined;
  }

  const minDepth = sortedDepths[0];
  const maxDepth = sortedDepths[sortedDepths.length - 1];

  if (depth < minDepth || depth > maxDepth) {
    return undefined;
  }

  const leftDepth = sortedDepths.filter((d) => d < depth).at(-1);
  const rightDepth = sortedDepths.find((d) => d > depth);

  if (leftDepth === undefined || rightDepth === undefined) {
    return undefined;
  }

  const leftRange = depthToXRange.get(leftDepth);
  const rightRange = depthToXRange.get(rightDepth);

  if (leftRange === undefined || rightRange === undefined) {
    return undefined;
  }

  const t = (depth - leftDepth) / (rightDepth - leftDepth);

  return {
    x0: leftRange.x0 + (rightRange.x0 - leftRange.x0) * t,
    x1: leftRange.x1 + (rightRange.x1 - leftRange.x1) * t,
  };
}
